import type { DatabaseSearchResult } from '@/models/DatabaseSearch'

// The CSV and the PDF carry these fields and nothing else, built from rows the
// user has already seen on screen.
export const EXPORT_COLUMNS = [
  'Funder',
  'Opportunity',
  'Link',
  'Application type',
  'Location',
  'Local areas',
  'Timing',
  'Tags',
] as const

export function exportRow(row: DatabaseSearchResult): string[] {
  return [
    row.funder,
    row.opportunity,
    row.link ?? '',
    row.application_type,
    row.location,
    row.location === 'Local' ? row.locations.join(', ') : '',
    row.timing,
    row.tags.join('; '),
  ]
}

function csvCell(value: string): string {
  // A leading = + - @ makes a spreadsheet run the cell as a formula.
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

export function selectionsCsv(rows: DatabaseSearchResult[]): string {
  const lines = [[...EXPORT_COLUMNS], ...rows.map(exportRow)].map((cells) =>
    cells.map(csvCell).join(','),
  )
  // The byte-order mark makes Excel read the file as UTF-8, so macrons survive.
  return '\uFEFF' + lines.join('\r\n')
}

/** `fundsorter-selections-YYYY-MM-DD.<ext>`, on the user's local date. */
export function exportFileName(ext: 'csv' | 'pdf', now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  return `fundsorter-selections-${day}.${ext}`
}

export function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking straight away can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function downloadSelectionsCsv(rows: DatabaseSearchResult[]): void {
  saveFile(
    new Blob([selectionsCsv(rows)], { type: 'text/csv;charset=utf-8' }),
    exportFileName('csv'),
  )
}
